import React, { useState, useRef, useEffect } from 'react';
import { Download, Table, FileSpreadsheet, Printer } from 'lucide-react';
import { formatDateBR } from '../../utils/dateUtils';

interface OsExportDropdownProps {
  ordens: any[];
}

export const OsExportDropdown: React.FC<OsExportDropdownProps> = ({ ordens }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cleanText = (val: any) => {
    if (val === null || val === undefined) return '';
    return String(val).replace(/[\r\n]+/g, ' ').trim();
  };

  const handleExportCSV = () => {
    if (!ordens || ordens.length === 0) {
      alert('Não há ordens de serviço para exportar com os filtros atuais.');
      return;
    }

    const headers = [
      'OS',
      'Cliente',
      'Status',
      'Data Atendimento',
      'Descricao',
      'Retirada',
      'Entrega',
      'Motorista',
      'Veiculo',
      'Valor Servico (R$)',
      'Qtd Ajudantes',
      'Valor Ajudante (R$)',
      'Total OS (R$)',
      'Total Repasses (R$)',
      'Valor Liquido (R$)',
      'Medicao Cliente',
      'Medicao Agregado',
    ];

    const rows = ordens.map((os) => [
      os.id,
      `"${cleanText(os.cliente_nome).replace(/"/g, '""')}"`,
      `"${cleanText(os.status_nome).replace(/"/g, '""')}"`,
      `"${formatDateBR(os.data_atendimento)}"`,
      `"${cleanText(os.descricao).replace(/"/g, '""')}"`,
      `"${cleanText(os.retirada).replace(/"/g, '""')}"`,
      `"${cleanText(os.entrega).replace(/"/g, '""')}"`,
      `"${cleanText(os.motorista_nome).replace(/"/g, '""')}"`,
      `"${cleanText(os.veiculo_nome).replace(/"/g, '""')}"`,
      Number(os.valor_unit_servi || 0).toFixed(2).replace('.', ','),
      os.quantidade_ajudante || 0,
      Number(os.valor_unit_ajud || 0).toFixed(2).replace('.', ','),
      Number(os.total_os || 0).toFixed(2).replace('.', ','),
      Number(os.valor_total_repasses || 0).toFixed(2).replace('.', ','),
      Number(os.valor_liquido || 0).toFixed(2).replace('.', ','),
      `"${cleanText(os.medicoes_id).replace(/"/g, '""')}"`,
      `"${cleanText(os.medicoes_agregado).replace(/"/g, '""')}"`,
    ]);

    const csvContent = [headers.join(';'), ...rows.map((e) => e.join(';'))].join('\r\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ordens_de_servico_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  const handleExportXLS = () => {
    if (!ordens || ordens.length === 0) {
      alert('Não há ordens de serviço para exportar com os filtros atuais.');
      return;
    }

    const totalGeral = ordens.reduce((acc, os) => acc + (Number(os.total_os) || 0), 0);
    const totalRepasses = ordens.reduce((acc, os) => acc + (Number(os.valor_total_repasses) || 0), 0);
    const totalLiquido = ordens.reduce((acc, os) => acc + (Number(os.valor_liquido) || 0), 0);

    const rowsHtml = ordens
      .map(
        (os) => `
      <tr style="height: 24px;">
        <td style="text-align: center; font-weight: bold; border: 1px solid #cbd5e1;">#${os.id}</td>
        <td style="border: 1px solid #cbd5e1;">${cleanText(os.cliente_nome)}</td>
        <td style="text-align: center; border: 1px solid #cbd5e1;">${cleanText(os.status_nome)}</td>
        <td style="text-align: center; border: 1px solid #cbd5e1;">${formatDateBR(os.data_atendimento)}</td>
        <td style="border: 1px solid #cbd5e1;">${cleanText(os.veiculo_nome)}</td>
        <td style="border: 1px solid #cbd5e1;">${cleanText(os.motorista_nome)}</td>
        <td style="border: 1px solid #cbd5e1;">${cleanText(os.descricao)}</td>
        <td style="border: 1px solid #cbd5e1;">${cleanText(os.retirada)}</td>
        <td style="border: 1px solid #cbd5e1;">${cleanText(os.entrega)}</td>
        <td style="text-align: right; border: 1px solid #cbd5e1; mso-number-format:'\\R\\$\\ #\\,##0\\.00';">${Number(os.total_os || 0).toFixed(2).replace('.', ',')}</td>
        <td style="text-align: right; border: 1px solid #cbd5e1; mso-number-format:'\\R\\$\\ #\\,##0\\.00';">${Number(os.valor_total_repasses || 0).toFixed(2).replace('.', ',')}</td>
        <td style="text-align: right; border: 1px solid #cbd5e1; font-weight: bold; mso-number-format:'\\R\\$\\ #\\,##0\\.00';">${Number(os.valor_liquido || 0).toFixed(2).replace('.', ',')}</td>
        <td style="text-align: center; border: 1px solid #cbd5e1;">${cleanText(os.medicoes_id) || '-'}</td>
        <td style="text-align: center; border: 1px solid #cbd5e1;">${cleanText(os.medicoes_agregado) || '-'}</td>
      </tr>
    `
      )
      .join('');

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8" />
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Ordens_de_Servico</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          body { font-family: Arial, sans-serif; font-size: 10pt; }
          .header-main { font-size: 15pt; font-weight: bold; color: #b91c1c; text-align: left; }
          .header-sub { font-size: 9pt; color: #64748b; }
          .th-cell { background-color: #0f172a; color: #ffffff; font-weight: bold; text-align: center; border: 1px solid #0f172a; padding: 8px; }
          .th-right { background-color: #0f172a; color: #ffffff; font-weight: bold; text-align: right; border: 1px solid #0f172a; padding: 8px; }
          .tot-row { background-color: #fee2e2; font-weight: bold; font-size: 11pt; border-top: 2px solid #b91c1c; }
        </style>
      </head>
      <body>
        <table>
          <tr>
            <td colspan="14" class="header-main">VOOPE TRANSPORTES - RELATÓRIO DE ORDENS DE SERVIÇO</td>
          </tr>
          <tr>
            <td colspan="14" class="header-sub">Total de O.S filtradas: ${ordens.length} | Exportado em: ${new Date().toLocaleString('pt-BR')}</td>
          </tr>
          <tr><td colspan="14"></td></tr>
          <thead>
            <tr>
              <th class="th-cell">Nº O.S</th>
              <th class="th-cell">Cliente</th>
              <th class="th-cell">Status</th>
              <th class="th-cell">Data Atendimento</th>
              <th class="th-cell">Veículo</th>
              <th class="th-cell">Motorista</th>
              <th class="th-cell">Descrição</th>
              <th class="th-cell">Local Retirada</th>
              <th class="th-cell">Local Entrega</th>
              <th class="th-right">Total O.S (R$)</th>
              <th class="th-right">Total Repasses (R$)</th>
              <th class="th-right">Valor Líquido (R$)</th>
              <th class="th-cell">Medição Cliente</th>
              <th class="th-cell">Medição Agregado</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
            <tr class="tot-row" style="height: 28px;">
              <td colspan="9" style="text-align: right; padding-right: 12px; font-weight: bold; border: 1px solid #cbd5e1;">TOTAIS GERAIS:</td>
              <td style="text-align: right; font-weight: bold; border: 1px solid #cbd5e1; mso-number-format:'\\R\\$\\ #\\,##0\\.00';">${totalGeral.toFixed(2).replace('.', ',')}</td>
              <td style="text-align: right; font-weight: bold; border: 1px solid #cbd5e1; mso-number-format:'\\R\\$\\ #\\,##0\\.00';">${totalRepasses.toFixed(2).replace('.', ',')}</td>
              <td style="text-align: right; font-weight: bold; border: 1px solid #cbd5e1; color: #15803d; mso-number-format:'\\R\\$\\ #\\,##0\\.00';">${totalLiquido.toFixed(2).replace('.', ',')}</td>
              <td colspan="2" style="border: 1px solid #cbd5e1;"></td>
            </tr>
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob(['\uFEFF' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ordens_de_servico_relatorio_${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setIsOpen(false);
  };

  const handlePrint = () => {
    window.print();
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 shadow-2xs cursor-pointer"
        title="Exportar dados da listagem de Ordens de Serviço"
      >
        <Download className="w-3.5 h-3.5 text-slate-500" />
        Exportar
      </button>

      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-52 rounded-xl shadow-xl bg-white border border-slate-200 ring-1 ring-black/5 divide-y divide-slate-100 z-30 focus:outline-none">
          <div className="py-1">
            <button
              onClick={handleExportXLS}
              className="w-full text-left px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer"
            >
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              Exportar para Excel (XLS)
            </button>
            <button
              onClick={handleExportCSV}
              className="w-full text-left px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer"
            >
              <Table className="w-4 h-4 text-slate-600" />
              Exportar para CSV (UTF-8)
            </button>
          </div>
          <div className="py-1">
            <button
              onClick={handlePrint}
              className="w-full text-left px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer"
            >
              <Printer className="w-4 h-4 text-blue-600" />
              Imprimir Relatório
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

